import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit6Notes: UnitNotes = {
  unitId: "unit-6",
  unitName: "Unit 6: Cities and Urban Land-Use Patterns and Processes",
  examWeight: "12–17%",
  focus: "Understanding urbanization, city hierarchies, urban models, and the challenges of urban growth.",
  sections: [
    {
      id: "topic-6-1",
      title: "Topic 6.1: Urbanization",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Defining Urbanization</h3>
            <div className="space-y-3">
              <KeyTerm term="Urbanization" definition="The process by which an increasing percentage of a population lives in cities and the suburbs of cities." />
              <KeyTerm term="Urban" definition="Relating to a city or town. Typically defined by population density and infrastructure." />
              <KeyTerm term="Suburbanization" definition="The growth of suburbs, areas on the outskirts of cities." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Global Urbanization Trends</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <ul className="text-sm text-slate-700 space-y-2">
                <li>📊 <strong>1800:</strong> 3% of world population lived in cities</li>
                <li>📊 <strong>1900:</strong> 14% of world population lived in cities</li>
                <li>📊 <strong>2000:</strong> 47% of world population lived in cities</li>
                <li>📊 <strong>2024:</strong> ~56% of world population lives in cities (projected to reach 68% by 2050)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Urbanization Patterns by Development Level</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏙️ MDCs (More Developed Countries)</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Already highly urbanized (70–90%)</li>
                  <li>Urbanization peaked in the 1970s–1980s</li>
                  <li>Now experiencing suburbanization and counter-urbanization</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🌍 LDCs (Less Developed Countries)</h4>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                  <li>Rapidly urbanizing (30–50%)</li>
                  <li>Urbanization accelerating due to rural-to-urban migration</li>
                  <li>Often creating megacities and slums</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Causes of Urbanization</h3>
            <div className="space-y-3">
              <KeyTerm term="Push Factors" definition="Rural areas: poverty, lack of jobs, limited services, environmental degradation." />
              <KeyTerm term="Pull Factors" definition="Cities: jobs, education, healthcare, entertainment, anonymity." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-2",
      title: "Topic 6.2: City Hierarchies and Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rank-Size Rule</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">In a country's city system, the largest city is twice the size of the second-largest city, three times the size of the third-largest city, and so on.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Example:</strong> If the largest city has 10 million people, the second-largest has 5 million, the third has 3.3 million, etc.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Reality:</strong> This rule is approximate; many countries deviate (especially those with primate cities).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Primate City</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">A city that is disproportionately larger than any other city in the country, often containing a large percentage of the nation's population, wealth, and power.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> Bangkok (Thailand), Paris (France), Mexico City (Mexico), Cairo (Egypt)</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Cause:</strong> Often a legacy of colonialism (colonial capitals) or centralized government.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Megacities and Megapolises</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <KeyTerm term="Megacity" definition="A city with a population of 10 million or more. Examples: Tokyo, Delhi, Shanghai, São Paulo." />
              <KeyTerm term="Megapolis (Megalopolis)" definition="A chain of cities and suburbs that have grown together to form one continuous urban area. Example: Boston-Washington corridor (BosWash)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Central Place Theory</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">Developed by Walter Christaller. Explains the distribution and size of cities based on the services they provide.</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                <li><strong>Central Place:</strong> A city that provides goods and services to surrounding areas.</li>
                <li><strong>Hierarchy:</strong> Larger cities provide more specialized services and have larger hinterlands (service areas).</li>
                <li><strong>Threshold:</strong> The minimum population needed to support a service.</li>
                <li><strong>Range:</strong> The maximum distance people will travel for a service.</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-3",
      title: "Topic 6.3: Global Cities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Defining Global Cities</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Cities that have a disproportionate influence on global affairs (politics, economics, culture). They are command centers of the global economy.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Characteristics:</strong> Headquarters of TNCs, stock exchanges, international organizations, cultural institutions, universities.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Tier 1 Global Cities</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🗽 New York</h4>
                <p className="text-xs text-slate-600 mt-1">Finance, media, culture</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 London</h4>
                <p className="text-xs text-slate-600 mt-1">Finance, law, media</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🗼 Tokyo</h4>
                <p className="text-xs text-slate-600 mt-1">Finance, technology, culture</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Tier 2 Global Cities</h3>
            <p className="text-sm text-slate-700 mb-3">Paris, Frankfurt, Hong Kong, Singapore, Shanghai, Dubai, Sydney, Toronto, Mexico City</p>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-4",
      title: "Topic 6.4: Urban Models",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concentric Zone Model (Burgess, 1925)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700 mb-3">Describes urban structure as concentric rings expanding outward from the CBD (Central Business District).</p>
              <div className="space-y-2">
                <p className="text-xs text-slate-600"><strong>Zone 1 (CBD):</strong> Downtown; retail, offices, entertainment</p>
                <p className="text-xs text-slate-600"><strong>Zone 2 (Transition):</strong> Older housing, light manufacturing, warehouses</p>
                <p className="text-xs text-slate-600"><strong>Zone 3 (Working Class):</strong> Residential; factory workers</p>
                <p className="text-xs text-slate-600"><strong>Zone 4 (Middle Class):</strong> Residential; single-family homes</p>
                <p className="text-xs text-slate-600"><strong>Zone 5 (Commuter):</strong> Suburbs; wealthy residential</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sector Model (Hoyt, 1939)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700 mb-3">Urban development follows transportation routes (highways, railroads) radiating from the CBD, creating wedge-shaped sectors.</p>
              <p className="text-xs text-slate-600"><strong>Key Idea:</strong> Similar land uses cluster together along transportation corridors (e.g., industrial zones along railroads).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Multiple Nuclei Model (Harris & Ullman, 1945)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700 mb-3">Cities have multiple centers (nuclei) of development, not just one CBD. Each nucleus specializes in a particular function.</p>
              <p className="text-xs text-slate-600"><strong>Example:</strong> A city might have a downtown CBD, a shopping mall nucleus, an industrial nucleus, and a university nucleus.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Limitations of Models</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">These models were developed for North American cities and don't apply well to:</p>
              <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4 mt-2">
                <li>Colonial cities (different spatial organization)</li>
                <li>Islamic cities (different cultural patterns)</li>
                <li>Rapidly urbanizing LDCs (informal settlements)</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-5",
      title: "Topic 6.5: Urban Land Use",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Zoning</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Government regulations that designate areas for specific uses (residential, commercial, industrial).</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Purpose:</strong> Prevent incompatible land uses from being adjacent (e.g., factories next to homes).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Gentrification</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">The process by which a neighborhood's character changes as wealthier people move in, renovate properties, and property values rise.</p>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> Reduced crime, improved infrastructure, tax revenue</p>
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Displacement of poor residents, loss of cultural identity</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Redlining</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">Historical practice where banks refused to lend money in certain neighborhoods (often minority neighborhoods), marked with red lines on maps.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Impact:</strong> Prevented wealth accumulation in these areas; legacy of segregation and inequality persists today.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">White Flight</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">The movement of white middle-class residents from cities to suburbs, often triggered by racial integration or urban decline.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Result:</strong> Concentration of poverty in inner cities; suburban sprawl.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-6",
      title: "Topic 6.6: Urban Infrastructure and Services",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Transportation</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🚗 Private Vehicles</h4>
                <p className="text-sm text-slate-700 mt-1">Dominant in MDCs; causes congestion, pollution, sprawl.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🚌 Public Transit</h4>
                <p className="text-sm text-slate-700 mt-1">Buses, trains, subways. More efficient; common in MDCs and large LDC cities.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🚲 Active Transport</h4>
                <p className="text-sm text-slate-700 mt-1">Walking, cycling. Sustainable; growing in popularity.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Housing</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏠 Formal Housing</h4>
                <p className="text-sm text-slate-700 mt-1">Legal, regulated, with proper utilities and services.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🏚️ Informal Housing (Slums)</h4>
                <p className="text-sm text-slate-700 mt-1">Illegal, unregulated, often lacking utilities. Common in LDCs.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Utilities</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700">💧 <strong>Water:</strong> Clean drinking water and sewage systems</p>
              <p className="text-sm text-slate-700">⚡ <strong>Electricity:</strong> Power grid</p>
              <p className="text-sm text-slate-700">🗑️ <strong>Waste Management:</strong> Garbage collection and disposal</p>
              <p className="text-sm text-slate-700">📡 <strong>Communications:</strong> Internet, phone networks</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-7",
      title: "Topic 6.7: Urban Challenges in LDCs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rapid Urbanization</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">LDCs are urbanizing faster than MDCs did, without the economic development to support it.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Result:</strong> Massive slums, inadequate services, unemployment.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Slums and Informal Settlements</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Densely populated areas with substandard housing, lacking basic services (water, sanitation, electricity).</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> Favelas (Brazil), Bidonvilles (Africa), Barrios (Latin America)</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Challenges:</strong> Disease, crime, pollution, no legal property rights.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Unemployment and Underemployment</h3>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-slate-700">Rural migrants often can't find formal jobs, leading to informal economy (street vending, day labor).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Environmental Issues</h3>
            <div className="space-y-3">
              <KeyTerm term="Air Pollution" definition="From vehicles, factories, and burning of waste. Causes respiratory diseases." />
              <KeyTerm term="Water Pollution" definition="Sewage and industrial waste contaminate water sources." />
              <KeyTerm term="Waste Management" definition="Inadequate garbage collection leads to piles of waste." />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-8",
      title: "Topic 6.8: Urban Sustainability",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sustainable Urban Development</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">Creating cities that meet the needs of the present without compromising the future.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Green Cities Initiatives</h3>
            <div className="space-y-3">
              <KeyTerm term="Green Spaces" definition="Parks, gardens, and trees that improve air quality and provide recreation." />
              <KeyTerm term="Green Buildings" definition="Energy-efficient buildings with sustainable materials and design." />
              <KeyTerm term="Renewable Energy" definition="Solar panels, wind turbines, geothermal systems." />
              <KeyTerm term="Public Transit" definition="Reducing car dependency through buses, trains, and bike lanes." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Smart Cities</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Cities that use technology (IoT, AI, data analytics) to improve efficiency and quality of life.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> Smart traffic lights, smart meters, real-time transit apps.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Urban Agriculture</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">Growing food in cities (rooftop gardens, community gardens, vertical farms) to improve food security and reduce food miles.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-9",
      title: "Topic 6.9: Suburbanization and Urban Sprawl",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Suburbanization</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">The growth of suburbs, areas on the outskirts of cities characterized by low-density, single-family homes.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Causes:</strong> Desire for space, car ownership, white flight, government policies (FHA loans).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Urban Sprawl</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">The uncontrolled expansion of urban areas into surrounding countryside, consuming agricultural land and natural habitats.</p>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <p className="text-xs text-slate-600"><strong>❌ Cons:</strong> Loss of farmland, habitat destruction, increased car dependency, pollution</p>
                <p className="text-xs text-slate-600"><strong>✅ Pros:</strong> More affordable housing, space for families</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Counter-Urbanization</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700">The movement of people away from large cities to smaller towns and rural areas, often due to remote work, quality of life, or cost of living.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-10",
      title: "Topic 6.10: Urban Inequality and Segregation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Residential Segregation</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">The spatial separation of different groups (by race, ethnicity, class) into different neighborhoods.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Causes:</strong> Discrimination, redlining, economic inequality, cultural preferences.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Ethnic Enclaves</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Neighborhoods where a particular ethnic group is concentrated.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Benefits:</strong> Cultural preservation, mutual support, economic opportunities.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Challenges:</strong> Can limit integration and economic mobility.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Inequality in Services</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">Wealthier neighborhoods have better schools, parks, police protection, and infrastructure than poorer neighborhoods.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-6-11",
      title: "Topic 6.11: Urban Culture and Identity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sense of Place in Cities</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700">Neighborhoods develop distinct identities based on history, architecture, demographics, and culture.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> Chinatown, Little Italy, the French Quarter (New Orleans)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Placelessness</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700">The loss of unique character in cities due to globalization and standardization.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Example:</strong> Every city has the same chain stores, restaurants, and architecture.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Gentrification and Cultural Loss</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">As neighborhoods gentrify, original residents are displaced, and cultural institutions (ethnic restaurants, churches, shops) are replaced by upscale establishments.</p>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit6Notes;